import React from 'react';

import { useResetRecoilState, useRecoilValue } from 'recoil';
import { CellProps } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useBanner } from '@tq/ui-components';

import { Team } from 'types';
import { useUpdateTeam } from 'api';
import { RowEditButtons } from '../RowEditButtons';
import { teamEditingRowIdState, teamUpdateRequestState } from 'store';
import { BANNER_TIMEOUT } from 'config';
import { useTeamCanUpdate } from '../../hooks';

interface TeamRowEditButtonsProps extends CellProps<Team, unknown> {
  width: number;
  emailRequired: boolean;
}

export const TeamRowEditButtons: React.FC<TeamRowEditButtonsProps> = ({
  emailRequired,
  row,
  width,
}) => {
  const { t } = useTranslation('create_organisation');
  const banner = useBanner();
  const updateRequest = useRecoilValue(teamUpdateRequestState);
  const resetEditingState = useResetRecoilState(teamEditingRowIdState);
  const disableUpdate = useTeamCanUpdate(emailRequired);

  const { mutate: updateTeam } = useUpdateTeam();
  const { teamId: id } = row.original;

  const handleUpdate = () => {
    updateTeam(
      { ...updateRequest, id },
      {
        onSuccess: () => {
          banner({
            id: 'update_team_success_id',
            title: t('team_update_success', 'Team has been updated'),
            duration: BANNER_TIMEOUT,
          });
          resetEditingState();
        },
        onError: () => {
          banner({
            id: 'update_team_fail_id',
            title: t('team_update_fail', 'The team could not be updated. Please try again.'),
            duration: BANNER_TIMEOUT,
            status: 'error',
          });
          resetEditingState();
        },
      }
    );
  };

  return (
    <RowEditButtons
      width={width}
      rowIndex={row.index}
      editingState={teamEditingRowIdState}
      disabled={disableUpdate}
      submit={handleUpdate}
      hideDelete
    />
  );
};
